import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Hero from '../components/Hero';
import CategoryShowcase from '../components/CategoryShowcase';
import LookbookGallery from '../components/LookbookGallery';
import BuyerCard from '../components/BuyerCard';
import ProcessSection from '../components/ProcessSection';
import DealershipSection from '../components/DealershipSection';
import ProductCard from '../components/ProductCard';
import company from '../data/company';
import { products, generateGeneralWhatsAppLink } from '../data/products';
import { ArrowRight, MessageSquare } from 'lucide-react';

const Home = () => {
  const featuredProducts = products.filter((p) => p.featured);

  return (
    <div className="page-home">
      {/* 01 HERO SECTION */}
      <Hero />

      {/* 02 CATEGORIES VISUAL SHOWCASE (VAN HEUSEN & AMERICAN EAGLE STYLE) */}
      <CategoryShowcase />

      {/* 03 FEATURED COLLECTIONS GRID */}
      <section style={{ padding: '80px 0', background: '#f8fafc', borderTop: '1px solid rgba(15, 23, 42, 0.06)', borderBottom: '1px solid rgba(15, 23, 42, 0.06)' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.6 }}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '24px', marginBottom: '48px' }}
          >
            <div>
              <span className="section-badge">CURATED ESSENTIALS</span>
              <h2 className="section-title" style={{ marginBottom: 0 }}>
                EXPORT <span className="gradient-text">COLLECTIONS</span>
              </h2>
            </div>
            <Link to="/collections" className="btn-secondary" style={{ padding: '12px 24px', fontSize: '13px' }}>
              <span>EXPLORE ALL STYLES</span>
              <ArrowRight size={16} />
            </Link>
          </motion.div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '28px'
            }}
          >
            {featuredProducts.map((product, idx) => (
              <ProductCard key={product.id} product={product} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* 04 HIGH-FASHION EDITORIAL LOOKBOOK */}
      <LookbookGallery />

      {/* 05 GLOBAL DEALERSHIP & DISTRIBUTORSHIP PROGRAM */}
      <DealershipSection />

      {/* 06 GLOBAL CORPORATE AUDIENCE */}
      <section id="corporate-intro" style={{ padding: '90px 0', position: 'relative' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center', maxWidth: '760px', margin: '0 auto 48px' }}
          >
            <span className="section-badge">WORLDWIDE BUYER AUDIENCE</span>
            <h2 className="section-title">
              BUILT FOR <span className="gradient-text">GLOBAL CORPORATE MARKETS</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              Supplying malls, multi-brand outlets (MBOs), retail chains, importers, distributors, and apparel brands with dependable denim collections.
            </p>
          </motion.div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px'
            }}
          >
            {company.targetAudience.map((buyer, idx) => (
              <BuyerCard key={buyer.id} item={buyer} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* 07 MANUFACTURING TIMELINE */}
      <ProcessSection />
      {/* 07 CORPORATE ENQUIRY SPOTLIGHT CTA */}
      <section style={{ padding: '80px 0', background: 'linear-gradient(135deg, rgba(241, 245, 249, 0.95) 0%, rgba(224, 0, 135, 0.08) 100%)', borderTop: '1px solid rgba(15, 23, 42, 0.08)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontFamily: 'var(--font-heading)', fontWeight: 900, marginBottom: '16px', color: 'var(--text-main)' }}>
              BUYING FOR MALLS, MBOs OR RETAIL STORES?
            </h2>
            <p style={{ fontSize: '17px', color: 'var(--text-muted)', maxWidth: '640px', margin: '0 auto 32px' }}>
              Connect with our export desk to discuss fits, colours, samples, wholesale quantities, replenishment, and international container shipments.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <motion.a whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} href={generateGeneralWhatsAppLink("Export Enquiry")} target="_blank" rel="noopener noreferrer" className="btn-whatsapp" style={{ padding: '16px 32px' }}>
                <MessageSquare size={18} />
                WHATSAPP EXPORT DESK
              </motion.a>
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                <Link to="/contact" className="btn-primary" style={{ padding: '16px 32px' }}>
                  SEND AN ENQUIRY
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
